import { useMemo } from "react";
import { ExtraInfoIcon } from "./ExtraInfoIcon";

export type ResultItem = {
  target: {
    id: string;
    description: string;
    value: number;
    extraInfo?: string;
  };
  dpi: number;
  cursorStep: number;
  gameErrorPct: number;
  cursorErrorPct: number;
};

export type OptimalResults = {
  gameResults: Array<{
    target: {
      id: string;
      description: string;
      value: number;
      extraInfo?: string;
    };
    result: {
      dpi: number;
      edpiErrorPct: number;
      windowsCursor: { windowsCursorStep: number; errorPct: number };
    };
  }>;
  cursorResults: Array<{
    target: {
      id: string;
      description: string;
      value: number;
      extraInfo?: string;
    };
    result: {
      dpi: number;
      windowsCursorStep: number;
      game: { errorPct: number };
    };
  }>;
};

function toResultTarget(target: OptimalResults["gameResults"][0]["target"]) {
  return {
    id: target.id,
    description: target.description,
    value: target.value,
    ...(target.extraInfo !== undefined ? { extraInfo: target.extraInfo } : {}),
  };
}

function errorColorClass(pct: number): string {
  if (pct < 1) return "text-sky-400";
  if (pct < 3) return "text-emerald-400";
  if (pct < 10) return "text-yellow-400";
  if (pct < 15) return "text-orange-400";
  return "text-red-400";
}

function ResultCard({ item }: { item: ResultItem }) {
  const { target, dpi, cursorStep, gameErrorPct, cursorErrorPct } = item;
  return (
    <li className="rounded-lg bg-black/20 p-3 border border-white/10 space-y-1">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-white font-medium text-lg">{target.value}</span>
          <span className="text-white/50 text-sm">{target.description}</span>
          {target.extraInfo && <ExtraInfoIcon extraInfo={target.extraInfo} />}
        </div>
        <div className="grid grid-cols-2 gap-2 mt-3">
          <div className="min-w-0 rounded-lg bg-white/5 border border-white/15 px-3 py-2.5 ring-1 ring-white/5 flex flex-col">
            <span className="text-white/40 text-[10px] uppercase tracking-wider font-medium block mb-0.5">
              DPI
            </span>
            <span className="text-white font-semibold tabular-nums">{dpi}</span>
            <span
              className={`text-xs mt-1.5 tabular-nums ${errorColorClass(gameErrorPct)}`}
            >
              Error: {gameErrorPct.toFixed(2)}%
            </span>
          </div>
          <div className="min-w-0 rounded-lg bg-white/5 border border-white/15 px-3 py-2.5 ring-1 ring-white/5 flex flex-col">
            <span className="text-white/40 text-[10px] uppercase tracking-wider font-medium block mb-0.5">
              Cursor step
            </span>
            <span className="text-white font-semibold tabular-nums">
              {cursorStep}
            </span>
            <span
              className={`text-xs mt-1.5 tabular-nums ${errorColorClass(cursorErrorPct)}`}
            >
              Error: {cursorErrorPct.toFixed(2)}%
            </span>
          </div>
        </div>
      </div>
    </li>
  );
}

export function Results({
  resultsTab,
  setResultsTab,
  gameDescription,
  cursorDescription,
  optimalResults,
}: {
  resultsTab: "game-edpi" | "windows-cursor";
  setResultsTab: (tab: "game-edpi" | "windows-cursor") => void;
  gameDescription: string;
  cursorDescription: string;
  optimalResults: OptimalResults;
}) {
  const { gameItems, cursorItems } = useMemo(() => {
    const gameItems: ResultItem[] = optimalResults.gameResults.map(
      ({ target, result }) => ({
        target: toResultTarget(target),
        dpi: result.dpi,
        cursorStep: result.windowsCursor.windowsCursorStep,
        gameErrorPct: result.edpiErrorPct,
        cursorErrorPct: result.windowsCursor.errorPct,
      }),
    );
    const cursorItems: ResultItem[] = optimalResults.cursorResults.map(
      ({ target, result }) => ({
        target: toResultTarget(target),
        dpi: result.dpi,
        cursorStep: result.windowsCursorStep,
        gameErrorPct: result.game.errorPct,
        cursorErrorPct: 0,
      }),
    );
    return { gameItems, cursorItems };
  }, [optimalResults]);

  const isGameEdpi = resultsTab === "game-edpi";
  const isWindowsCursor = resultsTab === "windows-cursor";
  const items = isGameEdpi ? gameItems : cursorItems;
  const description = isGameEdpi ? gameDescription : cursorDescription;
  return (
    <section className="rounded-xl bg-emerald-950/30 border border-emerald-500/20 p-4 space-y-4">
      <h2 className="text-sm font-semibold text-emerald-200 uppercase tracking-wider">
        Results
      </h2>

      <div className="flex gap-1 p-1 rounded-lg bg-black/20">
        <button
          type="button"
          onClick={() => setResultsTab("game-edpi")}
          className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
            isGameEdpi
              ? "bg-emerald-500/30 text-emerald-200"
              : "text-white/60 hover:text-white/80"
          }`}
        >
          Same game eDPI
        </button>
        <button
          type="button"
          onClick={() => setResultsTab("windows-cursor")}
          className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
            isWindowsCursor
              ? "bg-emerald-500/30 text-emerald-200"
              : "text-white/60 hover:text-white/80"
          }`}
        >
          Same Windows cursor speed
        </button>
      </div>

      <div className="space-y-3">
        <p className="text-xs text-white/50">{description}</p>
        <ul className="space-y-2">
          {items.map((item) => (
            <ResultCard key={item.target.id} item={item} />
          ))}
        </ul>
      </div>
    </section>
  );
}
